import { useState } from 'react';

import { Ban } from 'lucide-react';
import { toast } from 'sonner';

import { useDeletePrescriptionMutation } from '@/entities/rx';
import { ANALYTICS_EVENTS, analytics, logger } from '@/shared';
import { extractApiErrorDetail } from '@/shared/lib/errors/extractApiErrorDetail';
import { Button } from '@/shared/ui/shadcn/button';

import { CancelPrescriptionDialog } from './CancelPrescriptionDialog';

export interface CancelPrescriptionActionProps {
  userId: string;
  dosespotPrescriptionId: string;
  prescriptionName: string;
}

type CancelOutcome = 'success' | 'rejected' | 'error';

export function CancelPrescriptionAction({
  userId,
  dosespotPrescriptionId,
  prescriptionName,
}: CancelPrescriptionActionProps) {
  const [open, setOpen] = useState(false);
  const mutation = useDeletePrescriptionMutation();

  const trackResult = (outcome: CancelOutcome) => {
    void analytics.track({
      name: ANALYTICS_EVENTS.rx.prescriptionCancelResult,
      context: { actor: 'clinician', patientUserId: userId },
      properties: { dosespotPrescriptionId, outcome },
    });
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (mutation.isPending) return;
    setOpen(nextOpen);
  };

  const handleConfirm = async () => {
    void analytics.track({
      name: ANALYTICS_EVENTS.rx.prescriptionCancelAttempted,
      context: { actor: 'clinician', patientUserId: userId },
      properties: { dosespotPrescriptionId },
    });
    try {
      const result = await mutation.mutateAsync({ dosespotPrescriptionId, userId });
      if (result.success === true) {
        toast.success('Prescription cancelled.');
        trackResult('success');
        setOpen(false);
        return;
      }
      const description =
        result.message !== undefined && result.message !== null && result.message.trim() !== ''
          ? result.message
          : 'The cancellation could not be completed.';
      toast.error('Could not cancel prescription.', { description });
      trackResult('rejected');
    } catch (error: unknown) {
      logger.error('Failed to cancel prescription', { dosespotPrescriptionId, error });
      toast.error('Could not cancel prescription.', {
        description: extractApiErrorDetail(error) ?? 'Please try again.',
      });
      trackResult('error');
    }
  };

  return (
    <>
      <Button
        type="button"
        size="chip"
        aria-label={`Cancel order for ${prescriptionName}`}
        className="bg-red-700 text-white hover:bg-red-600 focus-visible:ring-red-500/40"
        onClick={() => {
          void analytics.track({
            name: ANALYTICS_EVENTS.rx.prescriptionCancelDialogOpened,
            context: { actor: 'clinician', patientUserId: userId },
            properties: { prescriptionIdPresent: dosespotPrescriptionId !== '' },
          });
          setOpen(true);
        }}
      >
        <Ban className="size-3.5" aria-hidden="true" />
        Cancel order
      </Button>

      <CancelPrescriptionDialog
        open={open}
        prescriptionName={prescriptionName}
        isPending={mutation.isPending}
        onConfirm={() => {
          void handleConfirm();
        }}
        onOpenChange={handleOpenChange}
      />
    </>
  );
}
